import { useCallback, useState, useMemo } from 'react'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useNFCSState } from 'modules/nfcs/hooks/useNFCSState'
import { useFirebase } from 'modules/analytics/firebase'
import { useNFCSModalAddState } from './useNFCSModalAddState'

import { Modal, ModalProps } from 'modules/modal/ui/Modal'
import { NFCSFormMint } from '../NFCSFormMint'
import { NFCSFormAdd } from '../NFCSFormAdd'
import { NFCSMintProgress } from '../NFCSMintProgress'
import { NFCSMintFinish } from '../NFCSMintFinish'
import { NFCSMintFailed } from '../NFCSMintFailed'
import { NFCSMintExitConfirm } from '../NFCSMintExitConfirm/NFCSMintExitConfirm'

import {
  NFCS_MODAL_CLOSE_EVENT,
  NFCS_ADDING_FORM_CLOSE_EVENT,
} from 'modules/analytics/events'

export function NFCSModal(props: ModalProps) {
  const { onClose } = props
  const { walletAddress } = useWeb3()
  const [pleaseDoNotLeave, setPlease] = useState(false)
  const {
    nfcs,
    tokenId,
    nfcsStatus,
    revalidateScore,
    signedAddresses,
    txHash: nfcsTxHash,
    setTxHash,
  } = useNFCSState()
  const { trackEvent } = useFirebase()

  const {
    txHash: txHashAdd,
    txStatus: txStatusAdd,
    showFormAddState,
    handleClickAddAddress,
    handleSubmitAdd,
  } = useNFCSModalAddState()

  const isEmptyFormMint = signedAddresses.length === 0

  const showState = useMemo(() => {
    if (pleaseDoNotLeave) return 'please'

    if (txStatusAdd.isFailed) return 'failed-add'
    if (txHashAdd && !txStatusAdd.isSuccess) return 'pending-add'
    if (showFormAddState) return 'form-add'

    if (nfcsStatus === 'failed') return 'failed-mint'
    if (nfcsStatus === 'generating') return 'pending-mint'
    if (
      nfcsStatus === 'not-generated' ||
      nfcsStatus === 'loading' ||
      !isEmptyFormMint
    ) {
      return 'form-mint'
    }
    return 'finish'
  }, [
    nfcsStatus,
    txHashAdd,
    isEmptyFormMint,
    pleaseDoNotLeave,
    showFormAddState,
    txStatusAdd.isFailed,
    txStatusAdd.isSuccess,
  ])

  const handleClose = useCallback(() => {
    if (showState === 'form-mint') {
      setPlease(true)
      return
    }
    onClose()
    if (showState === 'failed-mint') {
      revalidateScore()
    }
    trackEvent(
      showFormAddState ? NFCS_ADDING_FORM_CLOSE_EVENT : NFCS_MODAL_CLOSE_EVENT,
    )
  }, [showState, showFormAddState, revalidateScore, onClose, trackEvent])

  const handleStay = useCallback(() => setPlease(false), [])
  const handleTxSubmit = useCallback(
    (txHash: string) => setTxHash(txHash),
    [setTxHash],
  )

  return (
    <Modal
      width={showState === 'finish' ? 930 : 580}
      {...props}
      onClose={handleClose}
      withCloseOnClickOutside={false}
      withVerticalAlignment={showState === 'form-mint' ? false : true}
    >
      {showState === 'form-mint' && (
        <NFCSFormMint onTxSubmit={handleTxSubmit} />
      )}

      {showState === 'form-add' && <NFCSFormAdd onTxSubmit={handleSubmitAdd} />}

      {showState === 'please' && (
        <NFCSMintExitConfirm onStay={handleStay} onExit={handleClose} />
      )}

      {showState === 'pending-mint' && (
        <NFCSMintProgress title="Mint NFCS" onClose={onClose} />
      )}

      {showState === 'pending-add' && (
        <NFCSMintProgress title="Adding Addresses" onClose={onClose} />
      )}

      {showState === 'finish' && (
        <NFCSMintFinish
          nfcs={nfcs}
          tokenId={tokenId}
          walletAddress={walletAddress}
          isSelf
          onClose={onClose}
          onClickAdd={handleClickAddAddress}
        />
      )}

      {showState === 'failed-mint' && (
        <NFCSMintFailed
          title="NFCS Minting Failed"
          txHash={nfcsTxHash}
          onRetry={handleClose}
        />
      )}
      {showState === 'failed-add' && (
        <NFCSMintFailed
          title="Addresses Addition Failed"
          txHash={txHashAdd}
          onRetry={handleClose}
        />
      )}
    </Modal>
  )
}
